import { Box, Center, HStack, IconButton, VStack } from '@chakra-ui/react'
import { ArrowUp } from 'lucide-react'
import { useState, useRef } from 'react'
import SlashCommandMenu, { type SlashCommandResult } from './SlashCommandMenu'

// Ocean AI gradient colors (matching the kraken icon)
const KRAKEN_GRADIENT = 'linear-gradient(135deg, #7C3AED 0%, #A855F7 50%, #C084FC 100%)'
const KRAKEN_PRIMARY = '#7C3AED'
const KRAKEN_SECONDARY = '#A855F7'

interface KrakenChatBarProps {
  onSendMessage: (message: string) => void
  disabled?: boolean
  onFocusChange?: (isFocused: boolean) => void
}

const KrakenChatBar = ({ onSendMessage, disabled = false, onFocusChange }: KrakenChatBarProps) => {
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [slashCommand, setSlashCommand] = useState<'/item' | '/saw' | null>(null)
  const [slashQuery, setSlashQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = () => {
    if (inputValue.trim() && !disabled) {
      onSendMessage(inputValue.trim())
      setInputValue('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If a slash command menu is open, let it handle Enter (via its global keydown listener)
      if (!slashCommand) {
        handleSubmit()
      }
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)

    // Detect /item or /saw anywhere in the input
    const match = val.match(/\/(item|saw)\s*(.*)/i)
    if (match) {
      setSlashCommand(`/${match[1].toLowerCase()}` as '/item' | '/saw')
      setSlashQuery(match[2] || '')
    } else {
      setSlashCommand(null)
      setSlashQuery('')
    }
  }

  const handleSlashSelect = (result: SlashCommandResult) => {
    const prefix = result.type === 'item' ? 'item' : 'saw'
    const replaced = `Investigate ${prefix}_${result.id} (${result.title})`
    setInputValue(replaced)
    setSlashCommand(null)
    setSlashQuery('')
    inputRef.current?.focus()
  }

  const handleFocus = () => {
    setIsFocused(true)
    onFocusChange?.(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
    onFocusChange?.(false)
  }

  const canSubmit = inputValue.trim() && !disabled

  const chatBarShadow = '0 0 0 1px rgba(0, 0, 0, 0.06), 0 2px 8px -2px rgba(0, 0, 0, 0.08)'
  const chatBarHoverShadow = '0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 16px -4px rgba(0, 0, 0, 0.10)'
  const chatBarFocusShadow = '0 0 0 1.5px rgba(100, 100, 100, 0.2), 0 4px 16px -4px rgba(0, 0, 0, 0.10)'

  return (
    <Box
      w="100%"
      px={{ base: 3, md: 4 }}
      pb={{ base: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', md: 5 }}
      pt={2}
      flexShrink={0}
      position="relative"
      zIndex={10}
      bg="transparent"
    >
      <style>{`
        .kraken-input::placeholder {
          color: #a0a0a0;
        }
        @media (max-width: 767px) {
          .kraken-input { font-size: 16px !important; }
        }
      `}</style>
      <Center>
        <VStack gap={0} w="100%" maxW="680px" position="relative">
          <SlashCommandMenu
            command={slashCommand}
            searchQuery={slashQuery}
            onSelect={handleSlashSelect}
            onClose={() => { setSlashCommand(null); setSlashQuery('') }}
          />
          <Box
            w="100%"
            borderRadius="22px"
            bg="white"
            boxShadow={isFocused ? chatBarFocusShadow : chatBarShadow}
            _hover={{ boxShadow: isFocused ? chatBarFocusShadow : chatBarHoverShadow }}
            transition="box-shadow 0.3s ease"
            border="1px solid"
            borderColor={isFocused ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.08)'}
            px={4}
            py={2}
            pointerEvents="auto"
            onClick={() => inputRef.current?.focus()}
          >
            <HStack w="100%" gap={3} align="center">
              <input
                ref={inputRef}
                type="text"
                className="kraken-input"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="Ask Ocean AI..."
                disabled={disabled}
                style={{
                  flex: 1,
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#1a1a1a',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  height: '40px',
                  width: '100%',
                }}
              />
              <SendButton onClick={handleSubmit} disabled={!canSubmit} />
            </HStack>
          </Box>
        </VStack>
      </Center>
    </Box>
  )
}

interface SendButtonProps {
  onClick: () => void
  disabled: boolean
  isLoading?: boolean
}

const SendButton = ({ onClick, disabled, isLoading = false }: SendButtonProps) => {
  return (
    <IconButton
      aria-label="Send message"
      onClick={onClick}
      size="sm"
      variant="ghost"
      borderRadius="12px"
      disabled={disabled}
      minW={{ base: '36px', md: '32px' }}
      h={{ base: '36px', md: '32px' }}
      sx={{ touchAction: 'manipulation' }}
      bg={disabled ? 'gray.100' : 'gray.800'}
      _hover={{
        bg: disabled ? 'gray.100' : 'gray.700',
        transform: disabled ? 'none' : 'scale(1.04)',
        opacity: disabled ? 1 : 0.9,
      }}
      _active={{
        transform: 'scale(0.96)',
      }}
      css={{
        transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {isLoading ? (
        <Box
          w="18px"
          h="18px"
          borderRadius="full"
          border="2px solid"
          borderColor="gray.500"
          borderTopColor="transparent"
          animation="spin 0.8s linear infinite"
          css={{
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      ) : (
        <ArrowUp
          size={18}
          strokeWidth={2.5}
          style={{
            color: disabled ? 'rgba(0, 0, 0, 0.25)' : 'white',
          }}
        />
      )}
    </IconButton>
  )
}

export default KrakenChatBar
export { KRAKEN_GRADIENT, KRAKEN_PRIMARY, KRAKEN_SECONDARY }
